/**
 * Central IssueCode registry — the single source of truth for what each
 * `DriftIssue.code` means, how it's labelled, advised on, and (optionally)
 * auto-fixed.
 *
 * Replaces:
 *   - reporter.ts ISSUE_LABELS / getAdvice / getDelta (3 separate switches)
 *   - fix.ts FIXABLE_ISSUES / isFixActionAvailable / describeFixAction /
 *     applyFixAction (4 separate switches)
 *
 * Adding a new IssueCode: extend the union in types.ts, then add a matching
 * entry here. The `satisfies` clause enforces coverage at compile time.
 */
import chalk from "chalk";
import { join } from "node:path";
import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { globSync } from "glob";
import type { CaiConfig, DriftIssue, IssueCode, Severity } from "../types.js";

// ── Fix contract ─────────────────────────────────────────────────────────────

export interface FixContext {
  readonly config: CaiConfig;
  readonly issues: readonly DriftIssue[];
}

export interface FixResult {
  readonly changed: readonly string[];
}

export interface IssueFix {
  /** What files would change if we applied this fix (used for dry-run preview). */
  preview(ctx: FixContext): readonly string[];
  /** Apply the fix to disk. Returns the list of files actually modified. */
  apply(ctx: FixContext): Promise<FixResult> | FixResult;
}

// ── Spec shape ───────────────────────────────────────────────────────────────

export interface IssueDelta {
  readonly before: string;
  readonly after: string;
}

/** Public action names exposed in `cai fix` output and tests. Stable contract. */
export type FixActionName =
  | "sync-tool-configs"
  | "rebuild-pattern-index"
  | "document-scripts"
  | "normalize-path-references";

export interface IssueCodeSpec {
  readonly code: IssueCode;
  readonly label: string;
  readonly defaultSeverity: Severity;
  readonly advice: (issue: DriftIssue) => string;
  readonly delta?: (issue: DriftIssue) => IssueDelta | null;
  readonly fix?: IssueFix;
  /** Action name emitted in `cai fix` output. Distinct from code because
   *  multiple codes may share an action (e.g. INDEX_MISSING_ENTRY and
   *  INDEX_ORPHAN_ENTRY both → rebuild-pattern-index). */
  readonly action?: FixActionName;
}

// ── Helper builders ──────────────────────────────────────────────────────────

const advice = (s: string) => (_issue: DriftIssue): string => s;

// ── Individual code specs ────────────────────────────────────────────────────

const MISSING_PATH: IssueCodeSpec = {
  code: "MISSING_PATH",
  label: "Path no longer exists",
  defaultSeverity: "error",
  action: "normalize-path-references",
  advice: (issue) =>
    issue.suggestion ?? `Update this reference in ${chalk.cyan(issue.file)}, or create the missing file.`,
  delta: (issue) =>
    issue.claim?.value ? { before: issue.claim.value, after: "(not found in repo)" } : null,
  fix: {
    preview(ctx) {
      // Same target set as apply, but without writing
      return previewPathNormalization(ctx);
    },
    apply(ctx) {
      return applyPathNormalization(ctx);
    },
  },
};

// ── Inline implementations (kept here because they belong to the issue) ──────

import { isPathGitignored } from "./checkers/path.js";
import { syncToolConfigs } from "./checkers/tool-configs.js";
import { rebuildPatternIndex } from "../pattern/index.js";

interface PathTarget {
  file: string;
  from: string;
  to: string;
  line: number | null;
}

function pickScriptDocPath(config: CaiConfig): string {
  const preferred = ["context/setup.md", "SETUP.md"];
  for (const relativePath of preferred) {
    if (existsSync(join(config.scaffoldRoot, relativePath))) {
      return relativePath;
    }
  }
  return "context/setup.md";
}

function findPathNormalizationTargets(ctx: FixContext): PathTarget[] {
  const targets: PathTarget[] = [];
  const seen = new Set<string>();
  for (const issue of ctx.issues) {
    if (issue.code !== "MISSING_PATH" || !issue.claim) continue;
    if (issue.claim.intent === "example" || issue.claim.confidence === "low") continue;
    if (isPathGitignored(issue.claim.value, ctx.config.projectRoot)) continue;
    const match = resolveUniquePathMatch(ctx.config.projectRoot, issue.claim.value);
    if (!match || match === issue.claim.value) continue;
    const key = `${issue.file}:${issue.line}:${issue.claim.value}:${match}`;
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push({ file: issue.file, from: issue.claim.value, to: match, line: issue.line });
  }
  return targets;
}

function resolveUniquePathMatch(projectRoot: string, claimedPath: string): string | null {
  const segments = claimedPath.split("/");
  const fileName = segments[segments.length - 1];
  if (!fileName || fileName === claimedPath) return null;
  const matches = globSync(`**/${fileName}`, {
    cwd: projectRoot,
    ignore: ["node_modules/**", ".git/**", "dist/**", "build/**", ".cai/**", ".context-condensing/**"],
  });
  const unique = [...new Set(matches.map(toPosix))].sort();
  if (unique.length !== 1) return null;
  return unique[0];
}

function previewPathNormalization(ctx: FixContext): readonly string[] {
  const targets = findPathNormalizationTargets(ctx);
  return [...new Set(targets.map((t) => toPosix(t.file)))];
}

function applyPathNormalization(ctx: FixContext): FixResult {
  const targets = findPathNormalizationTargets(ctx);
  const changed = new Set<string>();
  for (const target of targets) {
    const absolutePath = join(ctx.config.scaffoldRoot, target.file);
    if (!existsSync(absolutePath)) continue;
    const content = readFileSync(absolutePath, "utf8");
    const updated = replacePathReference(content, target);
    if (updated === content) continue;
    writeFileSync(absolutePath, updated, "utf8");
    changed.add(toPosix(target.file));
  }
  return { changed: [...changed].sort() };
}

/** Normalize a relative path to forward slashes — keeps public output
 *  platform-independent. `relative()` returns backslashes on Windows. */
function toPosix(p: string): string {
  return p.split("\\").join("/");
}

function replacePathReference(content: string, target: { from: string; to: string; line: number | null }): string {
  const lines = content.split("\n");
  if (target.line && target.line > 0 && target.line <= lines.length) {
    const index = target.line - 1;
    const currentLine = lines[index];
    const updatedLine = replacePathReferenceInLine(currentLine, target.from, target.to);
    if (updatedLine !== currentLine) {
      lines[index] = updatedLine;
      return lines.join("\n");
    }
  }
  return content;
}

function replacePathReferenceInLine(line: string, from: string, to: string): string {
  const codeSpan = `\`${from}\``;
  if (line.includes(codeSpan)) return line.replaceAll(codeSpan, `\`${to}\``);
  const quoted = `"${from}"`;
  if (line.includes(quoted)) return line.replaceAll(quoted, `"${to}"`);
  const singleQuoted = `'${from}'`;
  if (line.includes(singleQuoted)) return line.replaceAll(singleQuoted, `'${to}'`);
  const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(?<![\\w/])${escaped}(?![\\w])`, "g");
  return line.replace(re, to);
}

// ── Other code specs ─────────────────────────────────────────────────────────

const TOOL_CONFIG_OUT_OF_SYNC: IssueCodeSpec = {
  code: "TOOL_CONFIG_OUT_OF_SYNC",
  label: "Tool config files are out of sync",
  defaultSeverity: "error",
  action: "sync-tool-configs",
  advice: (issue) =>
    `Run ${chalk.cyan("cai fix")} or ${chalk.cyan("cai sync-configs")} to re-sync tool config files.`,
  fix: {
    preview(ctx) {
      const files = new Set<string>();
      for (const issue of ctx.issues) {
        if (issue.code === "TOOL_CONFIG_OUT_OF_SYNC") files.add(issue.file);
      }
      return [...files];
    },
    apply(ctx) {
      const result = syncToolConfigs(ctx.config.projectRoot);
      return { changed: result.updated };
    },
  },
};

const INDEX_MISSING_ENTRY: IssueCodeSpec = {
  code: "INDEX_MISSING_ENTRY",
  label: "Pattern not listed in INDEX.md",
  defaultSeverity: "warning",
  action: "rebuild-pattern-index",
  advice: advice(`Run ${chalk.cyan("cai fix")} — it will add the missing entry automatically.`),
  fix: {
    preview() { return ["patterns/INDEX.md"]; },
    apply(ctx) {
      const result = rebuildPatternIndex(ctx.config);
      return { changed: [result.file] };
    },
  },
};

const INDEX_ORPHAN_ENTRY: IssueCodeSpec = {
  code: "INDEX_ORPHAN_ENTRY",
  label: "INDEX.md references a file that doesn't exist",
  defaultSeverity: "warning",
  action: "rebuild-pattern-index",
  advice: advice(`Run ${chalk.cyan("cai fix")} — it will remove the stale entry automatically.`),
  fix: {
    preview() { return ["patterns/INDEX.md"]; },
    apply(ctx) {
      const result = rebuildPatternIndex(ctx.config);
      return { changed: [result.file] };
    },
  },
};

const UNDOCUMENTED_SCRIPT: IssueCodeSpec = {
  code: "UNDOCUMENTED_SCRIPT",
  label: "Script not mentioned in scaffold",
  defaultSeverity: "warning",
  action: "document-scripts",
  advice: advice(`Run ${chalk.cyan("cai fix")} to add a stub entry, then fill in what the script does.`),
  fix: {
    preview(ctx) {
      const scripts = findUndocumentedScripts(ctx);
      return scripts.length > 0 ? [pickScriptDocPath(ctx.config)] : [];
    },
    apply(ctx) {
      const changed = addScriptDocumentationStubs(ctx);
      return { changed: changed ? [changed] : [] };
    },
  },
};

function findUndocumentedScripts(ctx: FixContext): string[] {
  const relativeDocPath = pickScriptDocPath(ctx.config);
  const absoluteDocPath = join(ctx.config.scaffoldRoot, relativeDocPath);
  if (!existsSync(absoluteDocPath)) return [];
  return [
    ...new Set(
      ctx.issues
        .filter((issue) => issue.code === "UNDOCUMENTED_SCRIPT")
        .map((issue) => issue.message.match(/Script "(.+?)"/)?.[1] ?? null)
        .filter((v): v is string => Boolean(v))
    ),
  ].sort();
}

function addScriptDocumentationStubs(ctx: FixContext): string | null {
  const scripts = findUndocumentedScripts(ctx);
  if (scripts.length === 0) return null;
  const relativeDocPath = pickScriptDocPath(ctx.config);
  const absoluteDocPath = join(ctx.config.scaffoldRoot, relativeDocPath);
  if (!existsSync(absoluteDocPath)) return null;
  const content = readFileSync(absoluteDocPath, "utf8");
  const updated = injectScriptBullets(content, scripts);
  if (updated === content) return relativeDocPath;
  writeFileSync(absoluteDocPath, updated, "utf8");
  return relativeDocPath;
}

function injectScriptBullets(content: string, scripts: readonly string[]): string {
  const heading = "## Common Commands";
  const existingScriptNames = scripts.filter((s) => content.includes(`\`${s}\``));
  const missingScripts = scripts.filter((s) => !existingScriptNames.includes(s));
  if (missingScripts.length === 0) return content;
  const lines = content.split("\n");
  const headingIndex = lines.findIndex((line) => line.trim() === heading);
  const bullets = missingScripts.map(
    (script) => `- \`${script}\` — [AUTO-FIX] document what this command does and when to use it`
  );
  if (headingIndex === -1) {
    return `${content}${content.endsWith("\n") ? "" : "\n"}\n${heading}\n${bullets.join("\n")}\n`;
  }
  let insertAt = headingIndex + 1;
  while (insertAt < lines.length && lines[insertAt].trim().startsWith("<!--")) {
    while (insertAt < lines.length && !lines[insertAt].includes("-->")) insertAt++;
    insertAt++;
  }
  while (insertAt < lines.length && lines[insertAt].trim() === "") insertAt++;
  lines.splice(insertAt, 0, ...bullets);
  return `${lines.join("\n")}${content.endsWith("\n") ? "" : "\n"}`;
}

// ── Codes without auto-fixes (advice only) ───────────────────────────────────

const STALE_FILE: IssueCodeSpec = {
  code: "STALE_FILE",
  label: "Scaffold file is out of date",
  defaultSeverity: "warning",
  advice: advice(`Run ${chalk.cyan("cai sync")} to let AI update this file, or ${chalk.cyan("cai fix")} for safe auto-fixes.`),
};

const DEAD_COMMAND: IssueCodeSpec = {
  code: "DEAD_COMMAND",
  label: "Script or command not found",
  defaultSeverity: "error",
  advice: (issue) =>
    issue.suggestion ?? `Update the command in ${chalk.cyan(issue.file)} to match an existing script, or add it to your package.json.`,
  delta: (issue) =>
    issue.claim?.value ? { before: issue.claim.value, after: "(not in package.json / Makefile)" } : null,
};

const DEPENDENCY_MISSING: IssueCodeSpec = {
  code: "DEPENDENCY_MISSING",
  label: "Claimed dependency not in manifest",
  defaultSeverity: "error",
  advice: (issue) =>
    issue.suggestion ?? `Remove the reference from ${chalk.cyan(issue.file)}, or add the package to your manifest.`,
  delta: (issue) =>
    issue.claim?.value ? { before: issue.claim.value, after: "(not in manifest)" } : null,
};

const VERSION_MISMATCH: IssueCodeSpec = {
  code: "VERSION_MISMATCH",
  label: "Version in scaffold doesn't match manifest",
  defaultSeverity: "warning",
  advice: (issue) =>
    issue.suggestion ?? `Update the version in ${chalk.cyan(issue.file)} to match what's actually installed.`,
  delta: (issue) => {
    if (!issue.claim?.value) return null;
    const match = issue.message.match(/manifest has version "(.+?)"/);
    return match ? { before: issue.claim.value, after: match[1] } : null;
  },
};

const CROSS_FILE_CONFLICT: IssueCodeSpec = {
  code: "CROSS_FILE_CONFLICT",
  label: "Conflicting information across files",
  defaultSeverity: "error",
  advice: advice(`Run ${chalk.cyan("cai sync")} so AI can reconcile the conflicting information.`),
};

const WORKSPACE_DEPENDENCY_MISSING: IssueCodeSpec = {
  code: "WORKSPACE_DEPENDENCY_MISSING",
  label: "Undocumented workspace dependency",
  defaultSeverity: "warning",
  advice: (issue) => issue.suggestion ?? "Document this dependency in your scaffold.",
};

const WORKSPACE_DEPENDENCY_INVALID: IssueCodeSpec = {
  code: "WORKSPACE_DEPENDENCY_INVALID",
  label: "Documented workspace dependency doesn't exist",
  defaultSeverity: "error",
  advice: (issue) => issue.suggestion ?? "Remove or correct this workspace dependency entry.",
};

const DEAD_EDGE: IssueCodeSpec = {
  code: "DEAD_EDGE",
  label: "Frontmatter link target doesn't exist",
  defaultSeverity: "error",
  advice: (issue) =>
    `Fix or remove the frontmatter \`edges:\` entry in ${chalk.cyan(issue.file)}.`,
};

const UNDOCUMENTED_ENV_VAR: IssueCodeSpec = {
  code: "UNDOCUMENTED_ENV_VAR",
  label: "Environment variable used but not documented",
  defaultSeverity: "warning",
  advice: (issue) =>
    issue.suggestion ?? `Document this environment variable in ${chalk.cyan(issue.file)}.`,
};

const RECURRING_CORRECTION: IssueCodeSpec = {
  code: "RECURRING_CORRECTION",
  label: "Recurring correction pattern detected",
  defaultSeverity: "warning",
  advice: advice(
    `Run ${chalk.cyan("cai pattern recurring")} to auto-generate a pattern from this recurring task type. Then run ${chalk.cyan("cai learn write-rule")} to codify the specific rule.`
  ),
};

const VERSION_PIN_MISMATCH: IssueCodeSpec = {
  code: "VERSION_PIN_MISMATCH",
  label: "Version mismatch across source files",
  defaultSeverity: "warning",
  advice: (issue) =>
    issue.suggestion ?? "Align all version pins for the same dependency across your config files.",
};

const BROKEN_LINK: IssueCodeSpec = {
  code: "BROKEN_LINK",
  label: "Markdown link target doesn't exist",
  defaultSeverity: "error",
  advice: (issue) =>
    issue.suggestion ?? `Fix or remove the link in ${chalk.cyan(issue.file)}.`,
};

const TODO_FIXME: IssueCodeSpec = {
  code: "TODO_FIXME",
  label: "TODO/FIXME marker in scaffold",
  defaultSeverity: "info",
  advice: (issue) =>
    issue.suggestion ?? `Resolve the TODO/FIXME in ${chalk.cyan(issue.file)}.`,
};

const DEAD_LINK: IssueCodeSpec = {
  code: "DEAD_LINK",
  label: "Markdown link target doesn't exist",
  defaultSeverity: "error",
  advice: (issue) =>
    issue.suggestion ?? `Fix or remove the link in ${chalk.cyan(issue.file)}.`,
};

const DEAD_ANCHOR: IssueCodeSpec = {
  code: "DEAD_ANCHOR",
  label: "Markdown link anchor has no matching heading",
  defaultSeverity: "warning",
  advice: (issue) =>
    issue.suggestion ?? `Update the anchor reference in ${chalk.cyan(issue.file)}.`,
};

const DEAD_RULE_GLOB: IssueCodeSpec = {
  code: "DEAD_RULE_GLOB",
  label: "Rule glob matches no files",
  defaultSeverity: "warning",
  advice: (issue) =>
    issue.suggestion ?? `Adjust the glob in ${chalk.cyan(issue.file)} to match existing paths.`,
};

const CHECKER_ERROR: IssueCodeSpec = {
  code: "CHECKER_ERROR",
  label: "Drift checker failed during execution",
  defaultSeverity: "warning",
  advice: advice(`A drift checker crashed. Run ${chalk.cyan("cai doctor")} to diagnose. This is usually a git or configuration issue.`),
};

// ── Registry ─────────────────────────────────────────────────────────────────

export const ISSUE_CODES = {
  STALE_FILE,
  MISSING_PATH,
  DEAD_COMMAND,
  DEPENDENCY_MISSING,
  VERSION_MISMATCH,
  CROSS_FILE_CONFLICT,
  WORKSPACE_DEPENDENCY_MISSING,
  WORKSPACE_DEPENDENCY_INVALID,
  DEAD_EDGE,
  INDEX_MISSING_ENTRY,
  INDEX_ORPHAN_ENTRY,
  UNDOCUMENTED_SCRIPT,
  UNDOCUMENTED_ENV_VAR,
  TOOL_CONFIG_OUT_OF_SYNC,
  RECURRING_CORRECTION,
  VERSION_PIN_MISMATCH,
  BROKEN_LINK,
  TODO_FIXME,
  DEAD_LINK,
  DEAD_ANCHOR,
  DEAD_RULE_GLOB,
  CHECKER_ERROR,
} as const satisfies Record<IssueCode, IssueCodeSpec>;

/** Find the spec for a given code. */
export function specFor(code: IssueCode): IssueCodeSpec {
  return ISSUE_CODES[code];
}

/** All codes that have an auto-fix attached. */
export function fixableCodes(): readonly IssueCode[] {
  return (Object.values(ISSUE_CODES) as IssueCodeSpec[])
    .filter((spec) => spec.fix !== undefined)
    .map((spec) => spec.code);
}